// Pure logic for the Contacts screen's District Affiliations section and for editing a roster record safely.
// No React, no network: everything here is unit-tested (scripts/test-contact-affiliations.ts) and rendered by
// components/DistrictAffiliationsSection.tsx and views/Contacts.tsx.
//
// Two ideas keep the screen honest:
//   1. Official registration (imported from my.Scouting), Compass-managed affiliations and the roster record are three different
//      things and are always labelled as such. Nothing here implies a manual affiliation is a registration.
//   2. A roster edit starts from the roster record's OWN district and stored Positions — never from the Person's combined
//      districts or from the Positions they hold elsewhere — and moves the record only when someone deliberately changes it.
import type { DirectoryAffiliation, DirectoryPerson } from '../types';

type EditBasisPerson = Pick<DirectoryPerson, 'districtIds' | 'positions' | 'contactDistrictId' | 'contactPositions'>;

/** Finds the current copy of a directory entry: by its Person when it has one, otherwise by its roster record (a roster record not
 *  yet linked to a Person). Used so an open panel shows the refreshed data after a change made inside it. */
export function currentDirectoryEntry<T extends Pick<DirectoryPerson, 'personId' | 'contactId'>>(list: readonly T[], open: T): T {
  const found = open.personId === null
    ? list.find((x) => x.contactId === open.contactId)
    : list.find((x) => x.personId === open.personId);
  return found ?? open;
}

/** What a roster edit starts from. A server that predates these fields falls back to the old behavior rather than breaking. */
export function rosterEditBasis(person: EditBasisPerson): { districtId: string | null; positions: string[] } {
  return {
    districtId: person.contactDistrictId !== undefined ? person.contactDistrictId : (person.districtIds[0] ?? null),
    positions: person.contactPositions !== undefined ? [...person.contactPositions] : [...person.positions],
  };
}

const norm = (id: string | null | undefined): string | null => (id ? id : null);

/** True only when the submitted district differs from the record's own. '' and null both mean "no district". */
export function districtMoved(initialDistrictId: string | null | undefined, submittedDistrictId: string | null | undefined): boolean {
  return norm(initialDistrictId) !== norm(submittedDistrictId);
}

/** Options for saving an existing roster record: `moveDistrict` is sent only for a deliberate move. */
export function rosterSaveOptions(initialDistrictId: string | null | undefined, submittedDistrictId: string | null | undefined): { moveDistrict?: true } {
  return districtMoved(initialDistrictId, submittedDistrictId) ? { moveDistrict: true } : {};
}

/** Whether the viewer may edit this Person's roster record: judged by the roster record's own district, so an affiliation in a
 *  district the viewer leads never offers them a record that belongs to another district. */
export function canEditRosterRecord(
  person: Pick<DirectoryPerson, 'contactId' | 'districtIds' | 'contactDistrictId'>,
  viewer: { isAdmin: boolean; canManage: (districtId: string) => boolean },
): boolean {
  if (person.contactId === null) return false;
  if (person.contactDistrictId !== undefined) {
    if (viewer.isAdmin) return true;
    return person.contactDistrictId !== null && viewer.canManage(person.contactDistrictId);
  }
  // Older server: the long-standing rule.
  return person.districtIds.some((id) => viewer.canManage(id)) || (viewer.isAdmin && person.districtIds.length === 0);
}

export interface RegistrationInfo {
  /** Names of the districts the my.Scouting import places this Person in. */
  districtNames: string[];
  /** Always true: these come from the import and are not edited here. */
  imported: true;
}
export function registrationInfo(person: Pick<DirectoryPerson, 'registeredDistrictIds'>, districtName: (id: string) => string): RegistrationInfo {
  return { districtNames: [...(person.registeredDistrictIds ?? [])].map(districtName).sort((a, b) => a.localeCompare(b)), imported: true };
}

export interface AffiliationRow {
  key: string;
  districtId: string;
  districtName: string;
  kind: 'manual' | 'orgChart';
  /** "Manual" or "Org Chart · Program Chair" */
  sourceLabel: string;
  description?: string;
  /** Manual rows only. */
  affiliationId?: string;
  /** Org Chart rows are changed by ending or reassigning the assignment, not here. */
  managedElsewhere: boolean;
}

export function affiliationRows(person: Pick<DirectoryPerson, 'affiliations'>, districtName: (id: string) => string): AffiliationRow[] {
  const rows = (person.affiliations ?? []).map((a: DirectoryAffiliation): AffiliationRow => {
    const manual = a.source === 'affiliation';
    return {
      key: manual ? `m:${a.affiliationId ?? a.districtId}` : `o:${a.assignmentId ?? `${a.districtId}:${a.positionId ?? ''}`}`,
      districtId: a.districtId,
      districtName: districtName(a.districtId),
      kind: manual ? 'manual' : 'orgChart',
      sourceLabel: manual ? 'Manual' : `Org Chart${a.positionName ? ` · ${a.positionName}` : ''}`,
      ...(a.description ? { description: a.description } : {}),
      ...(manual && a.affiliationId ? { affiliationId: a.affiliationId } : {}),
      managedElsewhere: !manual,
    };
  });
  return rows.sort((a, b) => a.districtName.localeCompare(b.districtName) || (a.kind === b.kind ? a.sourceLabel.localeCompare(b.sourceLabel) : a.kind === 'manual' ? -1 : 1));
}

/** Districts the Person is related to in some way the three lists above do not explain (an earlier roster record, say). */
export function otherDistrictIds(person: Pick<DirectoryPerson, 'districtIds' | 'registeredDistrictIds' | 'affiliations' | 'contactDistrictId'>): string[] {
  const explained = new Set<string>([...(person.registeredDistrictIds ?? []), ...(person.affiliations ?? []).map((a) => a.districtId)]);
  if (person.contactDistrictId) explained.add(person.contactDistrictId);
  return person.districtIds.filter((id) => !explained.has(id));
}

/** Whether to show the section at all. Hidden entirely for a council that has not enabled the feature, unless the Person already
 *  has affiliations (then they are shown read-only). */
export function showAffiliationsSection(person: Pick<DirectoryPerson, 'affiliations'>, enabled: boolean): boolean {
  return enabled || (person.affiliations ?? []).length > 0;
}

/** Whether the viewer sees add / edit / remove controls: council administrators only, for a real Person, while enabled. */
export function canManageAffiliations(person: Pick<DirectoryPerson, 'personId'>, viewer: { isAdmin: boolean }, enabled: boolean): boolean {
  return enabled && viewer.isAdmin && !!person.personId;
}

/** Districts still open for a NEW manual affiliation (one manual affiliation per district). */
export function districtsOpenForManualAdd(person: Pick<DirectoryPerson, 'affiliations'>, districts: Array<{ id: string; name: string }>): Array<{ id: string; name: string }> {
  const taken = new Set((person.affiliations ?? []).filter((a) => a.source === 'affiliation').map((a) => a.districtId));
  return districts.filter((d) => !taken.has(d.id));
}

export const MAX_AFFILIATION_DESCRIPTION = 500;
export function descriptionProblem(text: string): string | null {
  return text.trim().length > MAX_AFFILIATION_DESCRIPTION ? `Keep the description to ${MAX_AFFILIATION_DESCRIPTION} characters or fewer.` : null;
}

export type AffiliationRequest =
  | { operation: 'add'; districtIds: string[]; description?: string }
  | { operation: 'update'; affiliationId: string; description: string; districtId?: string }
  | { operation: 'remove'; affiliationId: string };

/** The request for the server, built from what the administrator entered. Returns an error message instead when it cannot be sent. */
export function buildAffiliationRequest(
  input:
    | { operation: 'add'; districtIds: string[]; description: string }
    | { operation: 'update'; row: Pick<AffiliationRow, 'affiliationId' | 'districtId' | 'description'>; districtId: string; description: string }
    | { operation: 'remove'; row: Pick<AffiliationRow, 'affiliationId'> },
): AffiliationRequest | { error: string } {
  if (input.operation === 'add') {
    if (input.districtIds.length === 0) return { error: 'Choose at least one district.' };
    const problem = descriptionProblem(input.description);
    if (problem) return { error: problem };
    const description = input.description.trim();
    return { operation: 'add', districtIds: [...new Set(input.districtIds)], ...(description ? { description } : {}) };
  }
  if (!input.row.affiliationId) return { error: 'This relationship is managed through its Org Chart assignment.' };
  if (input.operation === 'remove') return { operation: 'remove', affiliationId: input.row.affiliationId };
  const problem = descriptionProblem(input.description);
  if (problem) return { error: problem };
  const description = input.description.trim();
  const movedDistrict = input.districtId !== input.row.districtId;
  if (!movedDistrict && description === (input.row.description ?? '')) return { error: 'Nothing has changed.' };
  return { operation: 'update', affiliationId: input.row.affiliationId, description, ...(movedDistrict ? { districtId: input.districtId } : {}) };
}
